import * as AlgoSettingsActions from './algo-settings.actions';
import { Action, createReducer, on } from '@ngrx/store';
import {
  changeIsSameAsSpan,
  changesAreSame,
  IAlgoSettingState,
  IMetaCuratorRow,
  IMetadataPrompt,
  IMetadataReport,
  ISpan,
  ISpecFile
} from '../models/algo-settings.model';

export type State = IAlgoSettingState;

const initialState: State = {
  spanById: {},
  metaCuratorList: [],
  valueSuggestionByAdamType: {},
  columnLabelByAdamType: {},
  changes: {},
  currentChanges: {},
  lastProjectID: null,
  loading: false,
  inProgress: false,
  activeSpanKey: null,
  pollPrompts: false,
  specFiles: []
};

const algoSettingReducer = createReducer(
  initialState,
  on(AlgoSettingsActions.ClearState, (): State => initialState),
  on(AlgoSettingsActions.GetAlgoSettings, (state): State => ({ ...state, loading: true })),
  on(
    AlgoSettingsActions.GetAlgoSettingsSuccess,
    (state, { payload, projectID }): State => ({
      spanById: payload.spanById,
      metaCuratorList: payload.metaCuratorList,
      valueSuggestionByAdamType: payload.valueSuggestionByAdamType,
      columnLabelByAdamType: payload.columnLabelByAdamType,
      changes: payload.changes,
      currentChanges: {},
      loading: false,
      inProgress: false,
      lastProjectID: projectID,
      specFiles: state.specFiles
    })
  ),
  on(AlgoSettingsActions.GetAlgoSettingsFailed, (state): State => ({ ...state, loading: false })),
  on(AlgoSettingsActions.GetSpecFilesSuccess, (state, { specFiles }): State => ({ ...state, specFiles })),
  on(
    AlgoSettingsActions.GetSpecFileContentSuccess,
    (state, { fileID, data }): State => ({ ...state, specFiles: setSpecFileData(state.specFiles, fileID, data) })
  ),
  on(
    AlgoSettingsActions.GetSpecFileContentFailed,
    (state, { fileID }): State => ({ ...state, specFiles: setSpecFileData(state.specFiles, fileID, null) })
  ),
  on(AlgoSettingsActions.ApplyChanges, (state, { annotateID, changes }): State => {
    return { ...state, currentChanges: {}, changes: { ...state.changes, [annotateID]: changes } };
  }),
  on(AlgoSettingsActions.AlgoAccept, (state): State => ({ ...state, loading: true })),
  on(AlgoSettingsActions.AlgoAcceptFailed, (state): State => ({ ...state, loading: false })),
  on(AlgoSettingsActions.AlgoAcceptSuccess, (state, { tlfAnnotationID }): State => {
    const changes = { ...state.changes };
    const metaCuratorList = state.metaCuratorList.map((data) =>
      data._id === tlfAnnotationID ? { ...data, status: 'accept' } : data
    );
    delete changes[tlfAnnotationID];
    return { ...state, metaCuratorList, changes, loading: false };
  }),

  on(AlgoSettingsActions.AlgoRejectAccept, (state): State => ({ ...state, loading: true })),
  on(AlgoSettingsActions.AlgoRejectAcceptFailed, (state): State => ({ ...state, loading: false })),
  on(AlgoSettingsActions.AlgoRejectAcceptSuccess, (state, { tlfAnnotationID }): State => {
    const changes = { ...state.changes };
    const metaCuratorList = state.metaCuratorList.map((data) =>
      data._id === tlfAnnotationID ? { ...data, status: 'reject' } : data
    );
    delete changes[tlfAnnotationID];
    return { ...state, metaCuratorList, changes, loading: false };
  }),

  on(AlgoSettingsActions.AlgoAcceptFailed, (state): State => ({ ...state, loading: false })),
  // on(AlgoSettingsActions.AlgoAcceptAll, (state): State => ({ ...state, loading: true })),
  // on(AlgoSettingsActions.AlgoAcceptAllSuccess, (state): State => {
  //   return { ...state, changes: {}, spanById: {}, metaCuratorList: [], loading: false };
  // }),
  // on(AlgoSettingsActions.AlgoAcceptAllFailed, (state): State => ({ ...state, loading: false })),
  // on(AlgoSettingsActions.AlgoReprocess, (state): State => ({ ...state, loading: true })),
  // on(AlgoSettingsActions.AlgoReprocessSuccess, (state): State => {
  //   return { ...state, changes: {}, spanById: {}, metaCuratorList: [], loading: false, inProgress: true };
  // }),
  // on(AlgoSettingsActions.AlgoReprocessFailed, (state): State => ({ ...state, loading: false })),
  // on(AlgoSettingsActions.AlgoReprocessOneAnnotate, (state): State => ({ ...state, loading: true })),
  // on(AlgoSettingsActions.AlgoReprocessOneAnnotateSuccess, (state): State => {
  //   return { ...state, changes: {}, spanById: {}, metaCuratorList: [], loading: false, inProgress: true };
  // }),
  // on(AlgoSettingsActions.AlgoReprocessOneAnnotateFailed, (state): State => ({ ...state, loading: false })),
  on(
    AlgoSettingsActions.SetAlgoSettings,
    (state, { payload }): State => ({
      ...state,
      metaCuratorList: payload.data.map((row) => ({
        _id: row.id,
        name: row.fileName,
        number: row.tlfNumber,
        index: row.tlfIndex,
        originalTitle: row.originalTitle,
        checkKey: row.checkName,
        discrepancies: row.discrepanciesCount,
        missingColumns: row.missingColumns,
        tlfId: row.tlfId,
        status: row.status,
        assignees: row.assignees,
        sql: row.sql,
        supported: row.supported,
        tableType: row.tableType,
        adamTypes: row.adamTypes
      }))
    })
  ),
  on(AlgoSettingsActions.AddSpans, (state, { tlfAnnotationID, spans }): State => {
    const newSpans = { ...state.spanById[tlfAnnotationID] };
    for (const key in spans) {
      newSpans[key] = spans[key];
    }
    return {
      ...state,
      spanById: {
        ...state.spanById,
        [tlfAnnotationID]: newSpans
      }
    };
  }),

  on(AlgoSettingsActions.AddChange, (state, { tlfAnnotationID, change }): State => {
    const draftChanges = state.changes?.[tlfAnnotationID] ?? {};
    const currentChanges = { ...state.currentChanges };
    for (const key in change) {
      if (
        (draftChanges[key] && changesAreSame(draftChanges[key], change[key])) ||
        (!draftChanges[key] &&
          state.spanById[tlfAnnotationID]?.[key] &&
          changeIsSameAsSpan(change[key], state.spanById[tlfAnnotationID][key] as ISpan))
      ) {
        delete currentChanges[key];
      } else {
        currentChanges[key] = change[key];
      }
    }
    return { ...state, currentChanges };
  }),

  on(AlgoSettingsActions.ClearChanges, (state): State => {
    return { ...state, currentChanges: {} };
  }),

  on(AlgoSettingsActions.RemoveSpansByKeys, (state, { tlfAnnotationID, keys }): State => {
    const spans = { ...state.spanById[tlfAnnotationID] };
    keys.forEach((key) => delete spans[key]);
    return {
      ...state,
      spanById: {
        ...state.spanById,
        [tlfAnnotationID]: spans
      }
    };
  }),

  on(AlgoSettingsActions.SetActiveSpan, (state, { activeSpanKey }) => ({ ...state, activeSpanKey })),
  on(AlgoSettingsActions.ClearActiveSpan, (state) => ({ ...state, activeSpanKey: null })),

  on(AlgoSettingsActions.GetAlgoReportSuccess, (state, { report, tlfAnnotationID }) => {
    const metaCuratorList = updateCuratorsReport(state.metaCuratorList, tlfAnnotationID, report);
    return { ...state, metaCuratorList };
  }),
  on(AlgoSettingsActions.GetAlgoReportFailed, (state, { tlfAnnotationID }) => {
    const metaCuratorList = updateCuratorPrompts(state.metaCuratorList, { [tlfAnnotationID]: null });
    return { ...state, metaCuratorList };
  }),

  on(AlgoSettingsActions.SavePromptsSuccess, (state, { prompt, tlfAnnotationIDs }) => {
    const prompts = {};
    tlfAnnotationIDs.forEach((id) => (prompts[id] = prompt));
    const metaCuratorList = updateCuratorPrompts(state.metaCuratorList, prompts);
    return { ...state, metaCuratorList };
  }),

  on(AlgoSettingsActions.AckTableRegenerationSuccess, (state, { prompts, tlfAnnotationID }) => {
    const metaCuratorList = updateCuratorPrompts(state.metaCuratorList, { [tlfAnnotationID]: prompts });
    return { ...state, metaCuratorList };
  }),

  on(AlgoSettingsActions.PollPrompts, (state) => {
    return { ...state, pollPrompts: true };
  }),

  on(AlgoSettingsActions.StopPollPrompts, (state) => {
    return { ...state, pollPrompts: false };
  }),

  on(AlgoSettingsActions.PollPromptsSuccess, (state, { prompts }) => {
    const metaCuratorList = updateCuratorPrompts(state.metaCuratorList, prompts);
    return { ...state, metaCuratorList };
  })
);

export const reducer = (state: State, action: Action): State => algoSettingReducer(state, action);

const setSpecFileData = (specFiles: ISpecFile[], fileID: string, data: ArrayBuffer | null) =>
  specFiles.map((specFile) => (specFile.id === fileID ? { ...specFile, data } : specFile));

const updateCuratorsReport = (curatorRows: IMetaCuratorRow[], tlfAnnotationId: string, report: IMetadataReport) => {
  return curatorRows.map((row) => (row._id === tlfAnnotationId ? { ...row, report } : row));
};

const updateCuratorPrompts = (
  curatorRows: IMetaCuratorRow[],
  prompts: { [tlfAnnotationId: string]: IMetadataPrompt | null }
) => {
  return curatorRows.map((row) => {
    const newPrompts = prompts[row._id];
    return newPrompts !== undefined ? { ...row, prompts: newPrompts } : row;
  });
};
